// pages/DesignSystem.tsx
import { useState } from 'react';
import {
  Settings,
  Mail,
  Edit,
  Trash2,
  Search,
  Plus
} from 'lucide-react';
import {
  Button,
  Input,
  Select,
  Modal,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Badge,
  Toggle,
  Tabs,
  TabPanel,
  Dropdown,
  Avatar,
  Tooltip,
  Alert,
  SkeletonText,
  SkeletonCircle,
  SkeletonCard,
  EmptyState,
  ProgressBar,
  Breadcrumb,
  Divider
} from '../components/ui';

export default function DesignSystem() {
  const [activeTab, setActiveTab] = useState('buttons');
  const [toggleState, setToggleState] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState('');

  const tabs = [
    { id: 'buttons', label: 'Buttons' },
    { id: 'inputs', label: 'Inputs' },
    { id: 'cards', label: 'Cards' },
    { id: 'feedback', label: 'Feedback' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'misc', label: 'Misc' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Beaux Properties Design System</h1>
          <p className="text-gray-500 mt-1">Component library reference</p>
        </div>
      </header>

      {/* Color Palette */}
      <section className="bg-white border-b border-gray-200 px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Color Palette</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <ColorSwatch label="Primary" color="#2563eb" colorClass="bg-primary" />
            <ColorSwatch label="Primary Hover" color="#1d4ed8" colorClass="bg-primary-700" />
            <ColorSwatch label="Text" color="#111827" colorClass="bg-text" />
            <ColorSwatch label="Subtext" color="#6b7280" colorClass="bg-subtext" />
            <ColorSwatch label="Success" color="#10b981" colorClass="bg-success" />
            <ColorSwatch label="Warning" color="#f59e0b" colorClass="bg-warning" />
            <ColorSwatch label="Error" color="#ef4444" colorClass="bg-error" />
          </div>
        </div>
      </section>

      {/* Components */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

        <TabPanel tabId="buttons" activeTab={activeTab}>
          <Section title="Buttons">
            <div className="flex flex-wrap gap-4 mb-8">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="ghost">Ghost</Button>
            </div>

            <h4 className="font-medium text-gray-700 mb-3">Sizes</h4>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
            </div>

            <h4 className="font-medium text-gray-700 mb-3">States</h4>
            <div className="flex flex-wrap gap-4">
              <Button disabled>Disabled</Button>
              <Button isLoading>Loading</Button>
            </div>
          </Section>
        </TabPanel>

        <TabPanel tabId="inputs" activeTab={activeTab}>
          <Section title="Form Inputs">
            <div className="max-w-md space-y-4">
              <Input
                label="Email Address"
                placeholder="you@example.com"
                icon={<Mail size={18} />}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <Input
                label="With Error"
                placeholder="Enter something..."
                error="This field is required"
              />
              <Input
                label="Disabled"
                placeholder="Cannot edit"
                disabled
              />
              <Select
                label="Unit Type"
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                options={[
                  { value: '', label: 'Select a type...' },
                  { value: '1br', label: '1-BR' },
                  { value: '2br', label: '2-BR' },
                  { value: 'bachelor', label: 'Bachelor' },
                ]}
              />
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Toggle">
            <div className="space-y-4">
              <Toggle
                checked={toggleState}
                onChange={setToggleState}
                label="Enable notifications"
                description="Receive email updates about new units"
              />
              <Toggle
                checked={true}
                onChange={() => {}}
                label="Always on"
                disabled
              />
            </div>
          </Section>
        </TabPanel>

        <TabPanel tabId="cards" activeTab={activeTab}>
          <Section title="Card Variants">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <h3 className="font-medium">Basic Card</h3>
                </CardHeader>
                <CardBody>
                  <p className="text-gray-600">This is a basic card with header and body.</p>
                </CardBody>
              </Card>

              <Card hover onClick={() => alert('Clicked!')}>
                <CardHeader>
                  <h3 className="font-medium">Hoverable Card</h3>
                </CardHeader>
                <CardBody>
                  <p className="text-gray-600">Hover over me to see the effect.</p>
                </CardBody>
                <CardFooter>
                  <Button size="sm">Action</Button>
                </CardFooter>
              </Card>
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Skeleton Loading">
            <div className="grid md:grid-cols-2 gap-6">
              <SkeletonCard />
              <div className="space-y-4 p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-3">
                  <SkeletonCircle size={40} />
                  <SkeletonText lines={2} className="flex-1" />
                </div>
              </div>
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Empty State">
            <div className="bg-white rounded-lg border p-4">
              <EmptyState
                icon={<Search size={48} />}
                title="No units found"
                description="Try adjusting your search criteria or check back later for new listings."
                actionLabel="Clear Filters"
                onAction={() => {}}
              />
            </div>
          </Section>
        </TabPanel>

        <TabPanel tabId="feedback" activeTab={activeTab}>
          <Section title="Badges">
            <div className="flex flex-wrap gap-3 mb-6">
              <Badge variant="success">Available</Badge>
              <Badge variant="warning">Coming Soon</Badge>
              <Badge variant="error">Unavailable</Badge>
              <Badge variant="info">Featured</Badge>
              <Badge variant="neutral">Default</Badge>
            </div>
            <div className="flex flex-wrap gap-3">
              <Badge variant="success" size="md">Medium Badge</Badge>
              <Badge variant="info" size="sm">Small Badge</Badge>
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Alerts">
            <div className="space-y-4">
              <Alert variant="success" title="Success!" dismissible>
                Your changes have been saved successfully.
              </Alert>
              <Alert variant="warning" title="Warning">
                Please review the information before proceeding.
              </Alert>
              <Alert variant="error" title="Error">
                Something went wrong. Please try again.
              </Alert>
              <Alert variant="info">
                This is an informational message without a title.
              </Alert>
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Progress Bar">
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-600 mb-2">Determinate (75%)</p>
                <ProgressBar value={75} showValue />
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Indeterminate</p>
                <ProgressBar />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Success</p>
                  <ProgressBar value={100} color="success" size="sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Warning</p>
                  <ProgressBar value={60} color="warning" size="sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Error</p>
                  <ProgressBar value={30} color="error" size="sm" />
                </div>
              </div>
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Modal">
            <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
            <Modal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              title="Sample Modal"
              footer={
                <div className="flex gap-2 justify-end">
                  <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                  <Button onClick={() => setModalOpen(false)}>Confirm</Button>
                </div>
              }
            >
              <p className="text-gray-600">
                This is a sample modal dialog. You can put any content here, including forms,
                images, or other components.
              </p>
            </Modal>
          </Section>

          <Divider className="my-8" />

          <Section title="Spinner">
            <div className="flex items-center gap-8">
              <div className="text-center">
                <Spinner size="sm" />
                <p className="text-xs text-gray-500 mt-2">Small</p>
              </div>
              <div className="text-center">
                <Spinner size="md" />
                <p className="text-xs text-gray-500 mt-2">Medium</p>
              </div>
              <div className="text-center">
                <Spinner size="lg" />
                <p className="text-xs text-gray-500 mt-2">Large</p>
              </div>
            </div>
          </Section>
        </TabPanel>

        <TabPanel tabId="navigation" activeTab={activeTab}>
          <Section title="Tabs">
            <p className="text-gray-600 mb-4">Tabs are shown at the top of this page.</p>
          </Section>

          <Divider className="my-8" />

          <Section title="Breadcrumb">
            <div className="space-y-4">
              <Breadcrumb
                items={[
                  { label: 'Dashboard', href: '/admin' },
                  { label: 'Buildings', href: '/admin/buildings' },
                  { label: '66 Broadway' }
                ]}
                showHome
              />
              <Breadcrumb
                items={[
                  { label: 'Units', href: '#' },
                  { label: 'Unit 1204' }
                ]}
              />
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Dropdown Menu">
            <Dropdown
              trigger={
                <Button variant="secondary">
                  <span className="flex items-center gap-2">
                    Actions <Settings size={16} />
                  </span>
                </Button>
              }
              items={[
                { id: 'edit', label: 'Edit', icon: <Edit size={16} />, onClick: () => {} },
                { id: 'duplicate', label: 'Duplicate', icon: <Plus size={16} />, onClick: () => {} },
                'divider',
                { id: 'delete', label: 'Delete', icon: <Trash2 size={16} />, onClick: () => {}, danger: true },
              ]}
            />
          </Section>
        </TabPanel>

        <TabPanel tabId="misc" activeTab={activeTab}>
          <Section title="Avatar">
            <div className="flex items-center gap-4">
              <Avatar name="John Doe" size="sm" />
              <Avatar name="Jane Smith" size="md" />
              <Avatar name="Bob Wilson" size="lg" />
              <Avatar src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100" name="Photo" size="lg" />
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Tooltip">
            <div className="flex gap-8">
              <Tooltip content="Tooltip on top" position="top">
                <Button variant="secondary">Top</Button>
              </Tooltip>
              <Tooltip content="Tooltip on bottom" position="bottom">
                <Button variant="secondary">Bottom</Button>
              </Tooltip>
              <Tooltip content="Tooltip on left" position="left">
                <Button variant="secondary">Left</Button>
              </Tooltip>
              <Tooltip content="Tooltip on right" position="right">
                <Button variant="secondary">Right</Button>
              </Tooltip>
            </div>
          </Section>

          <Divider className="my-8" />

          <Section title="Divider">
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-500 mb-2">Simple Divider</p>
                <Divider />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">With Label</p>
                <Divider label="OR" />
              </div>
              <div className="flex items-center gap-4 h-8">
                <span className="text-gray-600">Item 1</span>
                <Divider orientation="vertical" />
                <span className="text-gray-600">Item 2</span>
                <Divider orientation="vertical" />
                <span className="text-gray-600">Item 3</span>
              </div>
            </div>
          </Section>
        </TabPanel>
      </div>
    </div>
  );
}

// Helper components
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ColorSwatch({ label, color, colorClass }: { label: string; color: string; colorClass: string }) {
  return (
    <div>
      <div className={`w-full h-16 rounded-lg ${colorClass} border border-gray-200`} />
      <p className="text-sm font-medium text-gray-700 mt-2">{label}</p>
      <p className="text-xs text-gray-500">{color}</p>
    </div>
  );
}
